import {useTranslation} from 'react-i18next'
import {useAppDispatch} from '../../../redux/hooks'
import {LecturerStatistics} from '../../../redux/Slice/lecturerSlice'
import {useEffect, useState} from 'react'
import Loader from 'app/custom/loader/Loader'
import ReactApexChart from 'react-apexcharts'
import {ApexOptions} from 'apexcharts'
import CustomSelect from 'app/custom/CustomSelect'
import {PlanningAcademicYears} from '../../../redux/Slice/generalSlice'

const LecStatistics = () => {
  const {t} = useTranslation()
  const dispatch = useAppDispatch()

  const [statistics, setStatistics] = useState<any>({})
  const [loader, setLoader] = useState(true)
  const [academicYears, setAcademicYears] = useState<any[]>([])
  const [selectedAy, setSelectedAy] = useState<number[]>([])

  const fetchData = (ayIds: number[]) => {
    setLoader(true)
    const fd = new FormData()
    ayIds.forEach((id) => fd.append('academic_year_id[]', id.toString()))

    dispatch(LecturerStatistics(fd)).then((res: any) => {
      if (res.meta.requestStatus === 'fulfilled') {
        setStatistics(res.payload)
      }
      setLoader(false)
    })
  }

  useEffect(() => {
    dispatch(PlanningAcademicYears()).then((res: any) => {
      if (res.meta.requestStatus === 'fulfilled') {
        const years = res.payload
        setAcademicYears(years)

        const sorted = [...years].sort((a: any, b: any) => a.name.localeCompare(b.name))

        const lastTwo = sorted.slice(-2)
        const selectedIds = lastTwo.map((y: any) => y.id)

        setSelectedAy(selectedIds)
        fetchData(selectedIds)
      }
    })
  }, [])

  const buildSideBySideChart = (categoryData: any, title: string) => {
    const years = Object.keys(categoryData || {})
    if (!years.length) return null

    const categorySet = new Set<string>()
    years.forEach((year) => {
      Object.keys(categoryData[year] || {}).forEach((cat) => {
        categorySet.add(cat)
      })
    })

    // 🔥 FORCE ORDER
    const preferredOrder = [
      'Doctoral',
      'Master',
      'Bachelor',
      'Other',
      'Male',
      'Female',
      'Swiss & European',
      'Non EU',
    ]

    const categories = preferredOrder.filter((item) => categorySet.has(item))

    const rawCounts: Record<string, number[]> = {}

    const series = categories.map((cat) => {
      rawCounts[cat] = []

      return {
        name: cat,
        data: years.map((year) => {
          const row = (categoryData[year] as Record<string, number>) || {}

          const total = (Object.values(row) as number[]).reduce((a, b) => a + Number(b), 0) || 1

          const count = Number(row[cat] ?? 0)
          rawCounts[cat].push(count)

          const percent = (count / total) * 100
          return parseFloat(percent.toFixed(2))
        }),
      }
    })

    const options: ApexOptions = {
      chart: {
        type: 'bar',
        toolbar: {show: false},
      },

      colors: categories.map((cat) => {
        const name = cat.toLowerCase()
        if (name === 'male') return '#0d6efd'
        if (name === 'female') return '#e83e8c'

        if (name === 'bachelor') return '#0d6efd'
        if (name === 'master') return '#20c997'
        if (name === 'doctoral') return '#6610f2'
        if (name === 'other') return '#fd7e14'

        if (name === 'swiss & european') return '#198754'
        if (name === 'non eu') return '#dc3545'

        return '#6c757d'
      }),

      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '50%',
        },
      },

      dataLabels: {
        enabled: true,
        formatter: (val: number) => (Number(val) > 0 ? `${Math.round(val)}%` : ''),
        style: {
          colors: ['#000'],
          fontSize: '12px',
          fontWeight: 600,
        },
      },

      xaxis: {
        categories: years,
        title: {
          text: '',
        },
      },

      yaxis: {
        max: 100,
        labels: {
          formatter: (val) => `${val}%`,
        },
      },

      legend: {
        position: 'bottom',
      },

      tooltip: {
        y: {
          formatter: function (val, opts) {
            const seriesName = opts.w.config.series[opts.seriesIndex].name
            const count = rawCounts[seriesName][opts.dataPointIndex]
            return `${count}`
          },
        },
      },

      title: {
        text: title,
        align: 'center',
      },

      grid: {
        borderColor: '#f1f1f1',
      },
    }

    return (
      <div className='col-12 mb-5'>
        <div className='card shadow-sm h-100'>
          <div className='card-body'>
            <ReactApexChart options={options} series={series} type='bar' height={350} />
          </div>
        </div>
      </div>
    )
  }

  if (loader) return <Loader />

  return (
    <>
      {/* FILTER SECTION */}
      <div className='card mb-5'>
        <div className='card-body'>
          <div className='row'>
            <div className='col-md-10'>
              <label className='form-label fw-bold required'>{t('LABEL.ACADEMIC_YEAR')}</label>
              <CustomSelect
                className='input'
                value={academicYears.filter((y) => selectedAy.includes(y.id))}
                onChange={(items: any) => setSelectedAy(items ? items.map((i: any) => i.id) : [])}
                options={academicYears}
                isMulti
              />
            </div>

            <div className='col-md-2 pt-7'>
              <button
                className='btn btn-outline-primary btn-sm fw-bold px-4 w-100'
                onClick={() => selectedAy.length > 0 && fetchData(selectedAy)}
              >
                <span className='fa fa-search mx-2'></span>
                {t('GLOBAL.SEARCH')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* CHARTS */}
      <div className='row'>
        {buildSideBySideChart(
          statistics.education_categories,
          t('DASHBOARD.PROFESSOR_QUALIFICATION')
        )}

        {buildSideBySideChart(statistics.gender_categories, t('DASHBOARD.PROFESSOR_GENDER'))}

        {buildSideBySideChart(statistics.diversity_categories, t('DASHBOARD.PROFESSOR_DIVERSITY'))}
      </div>
    </>
  )
}

export default LecStatistics
